import React from 'react';
import { Icon, Note } from '@repo/design-system';
import { visuallyHidden } from '@repo/ui';
import type { AdminTwin } from '@repo/frontend-types';

import PositionControls, {
  PositionLookupOutcome,
  type PositionControlsProps,
} from './PositionControls';
import SummaryCard, { SummaryRows, type SummaryRow } from './SummaryCard';
import { positionRows } from './positionOnMapRows';
import { isLocated, looksLikePlaceholderOrigin } from './twinDetailsForm';

/**
 * The summary's **Position and opening view** card (`twin-details-page-summary`, R6; frame 03).
 *
 * The rows are `positionRows`, the helper *Position on map* reads, so the two steps never disagree.
 * The summary is read-first: *Edit* opens the Position on map step, where the twin is moved or
 * turned (`retire-edit-mode-position-write-path`, R9). It does not wait on the save queue; that
 * step's own read does (R7). A twin with no real position
 * can still get one here: *Enter coordinates* shows for no position or the old placeholder, the same
 * controls and rules as the create route; saving the address sets it too
 * (`onboarding-persistent-scene-twin-details-map`, R3.3), so there is no *Find*. The address lookup's
 * outcome shows here whether or not the twin is placed (R3.4).
 *
 * The note, *Correcting the address never moves the twin*, shows only once the twin has a real
 * position: before that, saving the address is what places it (plan ruling 3).
 */

export const POSITION_NOTE =
  'Kept as they are. Correcting the address never moves the twin. Move or turn the twin on Position twin.';

interface Props extends Omit<PositionControlsProps, 'className' | 'onFind' | 'addressAction'> {
  /** The twin record, with the position as the step last saved it. */
  twin: AdminTwin;
  /** Opens the Position on map step (R9). */
  onOpenPositionOnMap: () => void;
}

const SummaryPositionCard: React.FC<Props> = ({ twin, onOpenPositionOnMap, ...controls }) => {
  const [position, orientation, view] = positionRows(twin);
  const located = isLocated(controls.saved);
  // A pair still being typed keeps its controls, even once a lookup has placed the twin meanwhile.
  const typing =
    controls.coords.latitude.trim() !== controls.saved.latitude.trim() ||
    controls.coords.longitude.trim() !== controls.saved.longitude.trim();
  const offerControls = !located || looksLikePlaceholderOrigin(controls.saved) || typing;
  const placed = !offerControls;

  const rows: SummaryRow[] = [
    {
      key: 'position',
      label: 'Position',
      value:
        located && orientation.value !== 'Not set'
          ? `${position.value} · orientation ${orientation.value}`
          : position.value,
      mono: located,
      tag: position.tag,
    },
    { key: 'view', label: 'Opening view', value: view.value, tag: view.tag },
  ];

  return (
    <SummaryCard
      surface="on-scene"
      title="Position and opening view"
      glyph="map-location-dot"
      discTone="neutral"
      onEdit={onOpenPositionOnMap}
      actionFirst
    >
      <SummaryRows rows={rows} />
      {/* Criterion 14: a saved position is announced politely when the value changes. The rows are
          not a live region, since the opening view would be announced with it. */}
      <p role="status" aria-live="polite" style={visuallyHidden}>
        {`Position: ${position.value}`}
      </p>
      {offerControls ? (
        <PositionControls {...controls} addressAction="none" />
      ) : (
        <PositionLookupOutcome state={controls.geocode.state} />
      )}
      {placed && (
        <Note tone="peri" icon={<Icon name="circle-info" size="xs" />}>
          {POSITION_NOTE}
        </Note>
      )}
    </SummaryCard>
  );
};

export default SummaryPositionCard;
